import { useState, useEffect, useContext } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { Button } from "../../catalyst-components/button";
import { Dialog, DialogTitle, DialogDescription, DialogActions } from "../../catalyst-components/dialog";
import { BaseContext } from "../../shared/base-context";
import { useNavigate } from "react-router-dom";
import { AdminRoutes, MainRoutes } from "../../shared/models";
import KnowledgeBaseManagement from "./KnowledgeBaseManagement";

type TargetAudience = "all" | "new_users" | "active_users";

interface FormState {
  typeId: string;
  title: string;
  content: string;
  categoryId: string;
  tagIds: string[];
  parentGroupId: string;
  sequenceInGroup: string;
  targetAudience: TargetAudience;
  isActive: boolean;
  sortOrder: string;
}

const initialFormState: FormState = {
  typeId: "",
  title: "",
  content: "",
  categoryId: "",
  tagIds: [],
  parentGroupId: "",
  sequenceInGroup: "",
  targetAudience: "all",
  isActive: true,
  sortOrder: "",
};

export default function KnowledgeBasePage() {
  const { convexUserId } = useContext(BaseContext);
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"knowledge_base"> | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<Id<"knowledge_base"> | null>(null);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [filterTypeId, setFilterTypeId] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showManagement, setShowManagement] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [tagInput, setTagInput] = useState("");
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  const entries = useQuery(api.knowledgeBase.getAllEntries) || [];
  const types = useQuery(api.knowledgeBaseMeta.getActiveTypes) || [];
  const categories = useQuery(api.knowledgeBaseMeta.getActiveCategories) || [];
  const tags = useQuery(api.knowledgeBaseMeta.getActiveTags) || [];
  const groups = useQuery(api.knowledgeBaseMeta.getActiveGroups) || [];
  
  const createEntry = useMutation(api.knowledgeBase.createEntry);
  const updateEntry = useMutation(api.knowledgeBase.updateEntry);
  const deleteEntry = useMutation(api.knowledgeBase.deleteEntry);
  const toggleActive = useMutation(api.knowledgeBase.toggleActive);
  const parseFile = useAction(api.importFAQ.parseFileContent);
  const importParsedData = useMutation(api.importFAQ.importParsedEntries);
  const seedStructure = useMutation(api.knowledgeBaseSeed.seedKnowledgeBaseStructure);

  // Seed if no types exist
  useEffect(() => {
    if (types.length === 0 && convexUserId) {
      seedStructure({ userId: convexUserId as Id<"users"> });
    }
  }, [types, convexUserId]);

  const selectedType = types.find(t => t._id === formState.typeId);

  const handleEdit = (entry: any) => {
    setEditingId(entry._id);
    setFormState({
      typeId: entry.typeId || "",
      title: entry.title,
      content: entry.content,
      categoryId: entry.categoryId || "",
      tagIds: entry.tagIds || [],
      parentGroupId: entry.parentGroupId || "",
      sequenceInGroup: entry.sequenceInGroup?.toString() || "",
      targetAudience: entry.targetAudience || "all",
      isActive: entry.isActive,
      sortOrder: entry.sortOrder?.toString() || "",
    });
  };

  const handleSave = async () => {
    if (!formState.typeId || !formState.title.trim() || !formState.content.trim()) {
      alert("Type, title, and content are required");
      return;
    }

    const payload = {
      typeId: formState.typeId as Id<"knowledge_base_types">,
      title: formState.title,
      content: formState.content,
      categoryId: formState.categoryId ? (formState.categoryId as Id<"knowledge_base_categories">) : undefined,
      tagIds: formState.tagIds.length > 0 ? (formState.tagIds as Id<"knowledge_base_tags">[]) : undefined,
      parentGroupId: formState.parentGroupId ? (formState.parentGroupId as Id<"knowledge_base_groups">) : undefined,
      sequenceInGroup: formState.sequenceInGroup ? parseInt(formState.sequenceInGroup) : undefined,
      targetAudience: formState.targetAudience || undefined,
      isActive: formState.isActive,
      sortOrder: formState.sortOrder ? parseInt(formState.sortOrder) : undefined,
    };

    if (editingId) {
      await updateEntry({ id: editingId, ...payload });
      setEditingId(null);
    } else {
      await createEntry(payload);
      setIsCreating(false);
    }

    setFormState(initialFormState);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormState(initialFormState);
  };

  const handleDelete = async () => {
    if (entryToDelete) {
      await deleteEntry({ id: entryToDelete });
      setIsDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  const handleToggleActive = async (id: Id<"knowledge_base">) => {
    await toggleActive({ id });
  };

  const handleAddTag = (tagId: string) => {
    if (!formState.tagIds.includes(tagId)) {
      setFormState({ ...formState, tagIds: [...formState.tagIds, tagId] });
    }
    setTagInput("");
    setShowTagSuggestions(false);
  };

  const handleRemoveTag = (tagId: string) => {
    setFormState({ ...formState, tagIds: formState.tagIds.filter(id => id !== tagId) });
  };

  // File import handlers
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setImportResult(null);
    setParsedData(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setFileContent(content);
    };
    reader.readAsText(selectedFile);
  };

  const handleParse = async () => {
    if (!fileContent) {
      setImportResult({ success: false, error: "No file content to parse" });
      return;
    }

    setParsing(true);
    setImportResult(null);
    setParsedData(null);

    try {
      const parsed = await parseFile({ fileContent });
      setParsedData(parsed);
    } catch (err: any) {
      setImportResult({ success: false, error: err.message });
    } finally {
      setParsing(false);
    }
  };

  const handleImportParsed = async () => {
    if (!convexUserId || !parsedData) {
      setImportResult({ success: false, error: "Missing data for import" });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const res = await importParsedData({
        userId: convexUserId as Id<"users">,
        entries: parsedData.entries,
      });
      setImportResult(res);

      if (res.success) {
        setFile(null);
        setFileContent("");
        setParsedData(null);
        setShowImport(false);
      }
    } catch (err: any) {
      setImportResult({ success: false, error: err.message });
    } finally {
      setImporting(false);
    }
  };

  const filteredTags = tags.filter(tag => 
    tag.displayName.toLowerCase().includes(tagInput.toLowerCase()) ||
    tag.name.toLowerCase().includes(tagInput.toLowerCase())
  );

  // Filter entries
  const filteredEntries = entries.filter((entry) => {
    const matchesType = filterTypeId === "all" || entry.typeId === filterTypeId;
    const matchesSearch = !searchTerm || 
      entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.categoryName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  const renderForm = () => (
    <div className="space-y-4 p-6 bg-white rounded-lg shadow mb-6">
      <h2 className="text-xl font-semibold mb-4">
        {editingId ? "Edit Entry" : "Create New Entry"}
      </h2>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
        <select
          className="w-full p-2 border rounded"
          value={formState.typeId}
          onChange={(e) => setFormState({ ...formState, typeId: e.target.value })}
        >
          <option value="">Select type...</option>
          {types.map((type) => (
            <option key={type._id} value={type._id}>
              {type.icon} {type.displayName}
            </option>
          ))}
        </select>
        {selectedType?.recommendedGranularity && (
          <p className="text-xs text-gray-500 mt-1 italic">
            💡 {selectedType.recommendedGranularity}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={formState.title}
          onChange={(e) => setFormState({ ...formState, title: e.target.value })}
          placeholder="Be specific - one concept per entry"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
        <textarea
          className="w-full p-2 border rounded min-h-[200px]"
          value={formState.content}
          onChange={(e) => setFormState({ ...formState, content: e.target.value })}
          placeholder="Keep focused on the title's topic"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            className="w-full p-2 border rounded"
            value={formState.categoryId}
            onChange={(e) => setFormState({ ...formState, categoryId: e.target.value })}
          >
            <option value="">No category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.displayName} ({cat.usageCount} uses)
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Parent Group</label>
          <select
            className="w-full p-2 border rounded"
            value={formState.parentGroupId}
            onChange={(e) => setFormState({ ...formState, parentGroupId: e.target.value })}
          >
            <option value="">No group</option>
            {groups.map((group) => (
              <option key={group._id} value={group._id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formState.tagIds.map(tagId => {
            const tag = tags.find(t => t._id === tagId);
            return tag ? (
              <span key={tagId} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm flex items-center gap-1">
                {tag.displayName}
                <button onClick={() => handleRemoveTag(tagId)} className="text-blue-600 hover:text-blue-800">×</button>
              </span>
            ) : null;
          })}
        </div>
        <div className="relative">
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={tagInput}
            onChange={(e) => {
              setTagInput(e.target.value);
              setShowTagSuggestions(e.target.value.length > 0);
            }}
            placeholder="Type to search tags..."
            onFocus={() => setShowTagSuggestions(tagInput.length > 0)}
          />
          {showTagSuggestions && filteredTags.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-40 overflow-y-auto">
              {filteredTags.map((tag) => (
                <button
                  key={tag._id}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  onClick={() => handleAddTag(tag._id)}
                >
                  {tag.displayName} <span className="text-xs text-gray-500">({tag.usageCount} uses)</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sequence in Group</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={formState.sequenceInGroup}
            onChange={(e) => setFormState({ ...formState, sequenceInGroup: e.target.value })}
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
          <select
            className="w-full p-2 border rounded"
            value={formState.targetAudience}
            onChange={(e) => setFormState({ ...formState, targetAudience: e.target.value as TargetAudience })}
          >
            <option value="all">All Users</option>
            <option value="new_users">New Users</option>
            <option value="active_users">Active Users</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={formState.sortOrder}
            onChange={(e) => setFormState({ ...formState, sortOrder: e.target.value })}
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          className="mr-2"
          checked={formState.isActive}
          onChange={(e) => setFormState({ ...formState, isActive: e.target.checked })}
        />
        <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
          Active (visible to AI)
        </label>
      </div>

      <div className="flex space-x-2 pt-4">
        <Button color="light" onClick={handleSave}>
          {editingId ? "Update" : "Create"}
        </Button>
        <Button color="light" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-24 p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Button color="light" onClick={() => navigate(MainRoutes.Admin)}>
            ← Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold">Knowledge Base</h1>
        </div>
        <div className="flex gap-2">
          <Button color="light" onClick={() => navigate(AdminRoutes.KnowledgeBaseAnalytics)}>
            📊 Analytics
          </Button>
          <Button color="light" onClick={() => setShowImport(!showImport)}>
            {showImport ? "Hide Import" : "📤 Import from File"}
          </Button>
          <Button color="light" onClick={() => setShowManagement(!showManagement)}>
            {showManagement ? "Hide" : "Manage"} Structure
          </Button>
          {!isCreating && !editingId && (
            <Button color="light" onClick={() => setIsCreating(true)}>
              Add New Entry
            </Button>
          )}
        </div>
      </div>

      {showManagement && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg">
          <KnowledgeBaseManagement />
        </div>
      )}

      {showImport && (
        <div className="mb-6 p-6 bg-white rounded-lg shadow space-y-4">
          <h2 className="text-xl font-semibold">Import from File</h2>
          
          {/* File Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              accept=".txt,.md"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center gap-2">
              <div className="text-4xl">📄</div>
              <div className="text-sm text-gray-600">
                Click to select a .txt or .md file
              </div>
              {file && (
                <div className="mt-2 text-sm font-medium text-blue-600">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </label>
          </div>

          {fileContent && (
            <>
              <div className="bg-gray-50 rounded border p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">File Preview</span>
                  <span className="text-xs text-gray-500">{fileContent.length} characters</span>
                </div>
                <pre className="text-xs text-gray-600 max-h-40 overflow-auto whitespace-pre-wrap">
                  {fileContent.substring(0, 500)}...
                </pre>
              </div>

              <Button
                color="light"
                onClick={handleParse}
                disabled={parsing}
                className="w-full"
              >
                {parsing ? "Parsing with AI..." : "Parse File"}
              </Button>
            </>
          )}

          {/* Parsing Status */}
          {parsing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-5 w-5" />
                <div>
                  <p className="font-semibold text-blue-900">AI is analyzing your file...</p>
                  <p className="text-sm text-blue-700">This may take 10-30 seconds</p>
                </div>
              </div>
            </div>
          )}

          {/* Parsed Data Preview */}
          {parsedData && (
            <div className="space-y-4">
              <h3 className="font-semibold">Parsed Data</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded p-4">
                  <div className="text-2xl font-bold text-blue-900">
                    {parsedData.summary.totalEntries}
                  </div>
                  <div className="text-sm text-blue-700">Total Entries</div>
                </div>
                <div className="bg-green-50 rounded p-4">
                  <div className="text-2xl font-bold text-green-900">
                    {Object.keys(parsedData.summary.byType).length}
                  </div>
                  <div className="text-sm text-green-700">Content Types</div>
                </div>
                <div className="bg-purple-50 rounded p-4">
                  <div className="text-2xl font-bold text-purple-900">
                    {Object.keys(parsedData.summary.byCategory).length}
                  </div>
                  <div className="text-sm text-purple-700">Categories</div>
                </div>
              </div>

              <div className="border rounded-lg max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Title</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-left">Category</th>
                      <th className="px-4 py-2 text-left">Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.entries.map((entry: any, idx: number) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{entry.title}</td>
                        <td className="px-4 py-2 text-gray-600">{entry.typeId}</td>
                        <td className="px-4 py-2 text-gray-600">{entry.categoryId || "—"}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {entry.tagIds?.length || 0} tags
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-4">
                <Button
                  color="light"
                  onClick={handleImportParsed}
                  disabled={importing}
                  className="flex-1"
                >
                  {importing ? "Importing..." : `Import ${parsedData.summary.totalEntries} Entries`}
                </Button>
                <Button
                  color="light"
                  onClick={() => {
                    setParsedData(null);
                    setFile(null);
                    setFileContent("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div
              className={`rounded-lg p-4 ${
                importResult.success
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              {importResult.success ? (
                <div className="text-green-800">
                  <p className="font-semibold mb-1">✅ Import Successful!</p>
                  <p className="text-sm">{importResult.message}</p>
                  <p className="text-sm mt-2">
                    Imported <strong>{importResult.count}</strong> entries.
                  </p>
                </div>
              ) : (
                <div className="text-red-800">
                  <p className="font-semibold mb-1">❌ Import Failed</p>
                  <p className="text-sm">{importResult.error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {(isCreating || editingId) && renderForm()}

      {!isCreating && !editingId && (
        <>
          {/* Filters */}
          <div className="mb-6 p-4 bg-white rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
                <select
                  className="w-full p-2 border rounded"
                  value={filterTypeId}
                  onChange={(e) => setFilterTypeId(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {types.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.icon} {type.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="Search title, content, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Entries Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEntries.map((entry) => (
                    <tr key={entry._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm">
                          {entry.typeInfo?.icon} {entry.typeName}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{entry.title}</div>
                        {entry.groupName && (
                          <div className="text-xs text-gray-500 mt-1">
                            Group: {entry.groupName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.categoryName ? (
                          <span 
                            className="px-2 py-1 rounded text-xs"
                            style={{ 
                              backgroundColor: entry.categoryInfo?.color + '20',
                              color: entry.categoryInfo?.color 
                            }}
                          >
                            {entry.categoryName}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {entry.tags?.slice(0, 3).map((tag: any) => (
                            <span key={tag._id} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                              {tag.displayName}
                            </span>
                          ))}
                          {entry.tags?.length > 3 && (
                            <span className="text-xs text-gray-500">+{entry.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {entry.timesUsed ? (
                            <>
                              <div className="font-medium">{entry.timesUsed}x used</div>
                              {entry.lastUsedAt && (
                                <div className="text-xs text-gray-500">
                                  Last: {new Date(entry.lastUsedAt).toLocaleDateString()}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">Never used</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            entry.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {entry.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <Button
                          color="light"
                          onClick={() => handleToggleActive(entry._id)}
                        >
                          {entry.isActive ? "Deactivate" : "Activate"}
                        </Button>
                        <Button color="light" onClick={() => handleEdit(entry)}>
                          Edit
                        </Button>
                        <Button
                          color="red"
                          onClick={() => {
                            setEntryToDelete(entry._id);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredEntries.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No entries found. {filterTypeId !== "all" || searchTerm ? "Try adjusting your filters." : ""}
              </div>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setEntryToDelete(null);
        }}
      >
        <DialogTitle>Delete Entry</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this entry? This action cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button
            color="light"
            onClick={() => {
              setIsDeleteDialogOpen(false);
              setEntryToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
